
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MetaVirtualAssistant = () => {
  return (
    <div className="font-['Raleway',sans-serif] bg-white text-gray-900 overflow-x-hidden pt-[90px]">
      
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative min-h-[300px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&q=80')`,
          }}
        />
        {/* Dark Blue Overlay */}
        <div className="absolute inset-0 bg-[#19354a]/90" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8"
          >
            Meta Virtual Assistant for Doctors
          </motion.h1>
          
          {/* Breadcrumb Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center gap-4 flex-wrap"
          >
            <Link 
              to="/" 
              className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold hover:bg-[#2a8db8] transition-all"
            >
              Home
            </Link>
            <span className="text-white text-2xl">|</span>
            <Link 
              to="/services" 
              className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold hover:bg-[#2a8db8] transition-all"
            >
              Services
            </Link>
            <span className="text-white text-2xl">|</span>
            <span className="px-8 py-3 bg-[#39a5d3] text-white rounded-full font-semibold">
              Meta Virtual Assistant
            </span>
          </motion.div>
        </div>
      </section>

      {/* CONTENT SECTION */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="/images/meta1.png" 
                alt="Meta Virtual Assistant for Healthcare"
                className="w-full max-w-[550px] h-auto mx-auto"
              />
            </motion.div>

            {/* Right - Content */}
            <div>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-gray-700 leading-relaxed mb-6"
              >
                In today's competitive healthcare landscape, managing your Meta (Facebook & Instagram) advertising campaigns requires constant attention, quick responses to patient inquiries, and expert optimization. Our Meta Virtual Assistant service provides healthcare practices with dedicated professionals who handle every aspect of your social media patient acquisition ecosystem.
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-gray-700 leading-relaxed mb-6"
              >
                From managing your ad campaigns and handling patient leads to responding to comments and DMs, our virtual assistants become an extension of your medical practice team. We ensure your <strong className="text-[#19354a]">Meta Ads campaigns</strong> run smoothly 24/7, maximizing your patient acquisition ROI while you focus on providing quality healthcare.
              </motion.p>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-gray-700 leading-relaxed mb-8"
              >
                At Docmize, we understand that every patient lead counts and every inquiry matters. Our trained Meta Virtual Assistants are equipped with healthcare-specific knowledge and tools to manage your social media presence professionally, ensuring no patient opportunity slips through the cracks. Let us handle the day-to-day operations while you scale your practice to new heights.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Link 
                  to="/contact" 
                  className="inline-block px-8 py-4 bg-[#19354a] text-white rounded-lg font-semibold hover:bg-[#0f2133] transition-all"
                >
                  Get Started Today
                </Link>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      

{/* WHY CHOOSE US - BLUE SECTION */}
<section className="relative py-20 overflow-hidden">
  {/* Dark Blue Background */}
  <div 
    className="absolute inset-0"
    style={{
      background: 'linear-gradient(135deg, #0f1c3f 0%, #1a2d5e 50%, #152349 100%)'
    }}
  />
  
  {/* Diagonal Lines Pattern */}
  <div className="absolute inset-0 overflow-hidden">
    {/* Main diagonal lines */}
    <div 
      className="absolute inset-0"
      style={{
        backgroundImage: `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 50px,
          rgba(59, 130, 246, 0.1) 50px,
          rgba(59, 130, 246, 0.1) 52px
        )`
      }}
    />
    {/* Additional accent lines */}
    <div className="absolute top-0 left-[20%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent transform rotate-[30deg] origin-top"></div>
    <div className="absolute top-0 left-[40%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-400/30 to-transparent transform rotate-[30deg] origin-top"></div>
    <div className="absolute top-0 left-[60%] w-[3px] h-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent transform rotate-[30deg] origin-top"></div>
    <div className="absolute top-0 left-[80%] w-[2px] h-full bg-gradient-to-b from-transparent via-blue-400/15 to-transparent transform rotate-[30deg] origin-top"></div>
  </div>

  {/* Content */}
  <div className="relative z-10 max-w-7xl mx-auto px-6">
    
    {/* Section Header */}
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
    >
      <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4">
        Why Choose Our Meta VA Services for Healthcare?
      </h2>
      <p className="text-white/70 text-lg">
        Here's why doctors and hospitals trust us with their patient acquisition:
      </p>
    </motion.div>

    {/* Benefits Grid - 2 Columns */}
    <div className="grid md:grid-cols-2 gap-x-16 gap-y-8 max-w-5xl mx-auto">
      
      {/* Left Column */}
      <div className="space-y-8">
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Dedicated healthcare Meta Ads specialists managing your campaigns 24/7</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Real-time patient lead management and instant inquiry handling</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Professional comment and DM management for better patient engagement</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Seamless appointment booking and patient follow-up automation</p>
        </motion.div>
      </div>

      {/* Right Column */}
      <div className="space-y-8">
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">HIPAA-compliant patient data handling and CRM updates</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Daily ad monitoring with healthcare-specific optimization suggestions</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Detailed patient acquisition reports and actionable insights</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex items-start gap-4"
        >
          <span className="text-[#39a5d3] text-2xl font-bold">»</span>
          <p className="text-white text-lg">Expert pixel setup, event tracking, and patient conversion optimization</p>
        </motion.div>
      </div>

    </div>
  </div>
</section>

      {/* OUR SERVICES SECTION */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-4">
              Our Meta VA Services for Healthcare
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive virtual assistant solutions for all your healthcare Meta advertising needs
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Service 1 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m4 0H3a1 1 0 00-1 1v1a1 1 0 001 1h18a1 1 0 001-1V5a1 1 0 00-1-1zM5 8v11a2 2 0 002 2h10a2 2 0 002-2V8" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Healthcare Meta Ads VA</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Dedicated virtual assistants specializing in Facebook & Instagram ad management for doctors, hospitals, and medical practices.
              </p>
            </motion.div>

            {/* Service 2 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Patient Lead Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Efficient patient lead capture, qualification, and HIPAA-compliant response handling to ensure no inquiry is left unattended.
              </p>
            </motion.div>

            {/* Service 3 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Comment & DM Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Professional handling of all patient comments and direct messages to maintain engagement and build trust.
              </p>
            </motion.div>

            {/* Service 4 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Appointment Booking</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Streamlined patient appointment scheduling and automated follow-ups to convert leads into booked consultations.
              </p>
            </motion.div>

            {/* Service 5 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 8l4 4m0-4l-4 4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">CRM Updates & Patient Tracking</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Keep your healthcare CRM up-to-date with accurate patient information and comprehensive tracking for better conversions.
              </p>
            </motion.div>

            {/* Service 6 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Ad Monitoring & Optimization</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Continuous monitoring of your healthcare ad performance with optimization to maximize patient acquisition ROI.
              </p>
            </motion.div>

            {/* Service 7 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.7 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Reporting & Performance Checks</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Daily performance reports and comprehensive analytics to keep you informed about your patient acquisition progress.
              </p>
            </motion.div>

            {/* Service 8 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.8 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Page & Business Manager</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Complete management of your healthcare Facebook Page and Business Manager for seamless advertising operations.
              </p>
            </motion.div>

            {/* Service 9 */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.9 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl p-6 border-2 border-[#19354a] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#19354a] rounded-xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#19354a] mb-3">Pixel & Conversion Tracking</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Expert setup and management of Meta Pixel, custom events, and patient conversion tracking for accurate attribution.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

     

      {/* BENEFITS SECTION */}
<section className="py-16 bg-white">
  <div className="max-w-7xl mx-auto px-6">
    
    {/* Section Header */}
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-16"
    >
      <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#19354a] mb-4">
        Reap the Benefits of Meta VA Services
      </h2>
      <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-6">
        at <span className="text-[#19354a]">Docmize</span>
      </h3>
      <p className="text-gray-600 max-w-4xl mx-auto leading-relaxed">
        Renowned as the best healthcare Meta advertising partner, we specialize in crafting tailored strategies that elevate your practice's social presence, increase patient conversions, and optimize ROI. With Docmize, experience the power of dedicated virtual assistants and watch your patient base soar to new heights.
      </p>
    </motion.div>

    {/* Benefit 1 - Image Right */}
    <div className="grid lg:grid-cols-2 gap-0 mb-8">
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-[#fff4e6] p-6 lg:p-10 flex items-center border-l-4 border-[#19354a]"
      >
        <div>
          <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-4">
            Define Your Patient Acquisition Goals
          </h3>
          <p className="text-gray-700 leading-relaxed">
            We initiate the process by determining your healthcare Meta advertising goals, reviewing historical patient data, and curating a clear strategy that meets your practice requirements. Our team works closely with you to understand your target patient demographics and specialty-specific objectives.
          </p>
        </div>
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-[#e8f4fc]"
      >
        <img 
          src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
          alt="Define Campaign Goals"
          className="w-full h-full min-h-[200px] object-cover"
        />
      </motion.div>
    </div>

    {/* Benefit 2 - Image Left */}
    <div className="grid lg:grid-cols-2 gap-0 mb-8">
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-[#e6f7f0] order-2 lg:order-1"
      >
        <img 
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
          alt="Patient Lead Management"
          className="w-full h-full min-h-[200px] object-cover"
        />
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-[#e6f2e6] p-6 lg:p-10 flex items-center order-1 lg:order-2 border-r-4 border-[#E85A2C]"
      >
        <div>
          <h3 className="text-2xl lg:text-3xl font-bold text-[#E85A2C] mb-4">
            Patient Lead Management & Response
          </h3>
          <p className="text-gray-700 leading-relaxed">
            Our virtual assistants handle every patient inquiry with precision and HIPAA compliance. From instant response handling to qualifying prospective patients, we ensure no opportunity slips through. Real-time notifications and systematic follow-ups keep your patient pipeline flowing smoothly.
          </p>
        </div>
      </motion.div>
    </div>

    {/* Benefit 3 - Image Right */}
    <div className="grid lg:grid-cols-2 gap-0 mb-8">
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-[#f0e6f7] p-6 lg:p-10 flex items-center border-l-4 border-[#19354a]"
      >
        <div>
          <h3 className="text-2xl lg:text-3xl font-bold text-[#19354a] mb-4">
            Implement Tracking & Analytics
          </h3>
          <p className="text-gray-700 leading-relaxed">
            After identifying your campaign metrics and establishing the ad structure, we implement advanced tracking options. Our experts set up Meta Pixel, custom events, and patient conversion tracking to measure every interaction accurately while maintaining HIPAA compliance.
          </p>
        </div>
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-[#e6e9f7]"
      >
        <img 
          src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80" 
          alt="Tracking Analytics"
          className="w-full h-full min-h-[200px] object-cover"
        />
      </motion.div>
    </div>

    {/* Benefit 4 - Image Left */}
    <div className="grid lg:grid-cols-2 gap-0">
      <motion.div 
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-[#fce8e8] order-2 lg:order-1"
      >
        <img 
          src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80" 
          alt="Optimize Performance"
          className="w-full h-full min-h-[200px] object-cover"
        />
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-[#fff0e6] p-6 lg:p-10 flex items-center order-1 lg:order-2 border-r-4 border-[#E85A2C]"
      >
        <div>
          <h3 className="text-2xl lg:text-3xl font-bold text-[#E85A2C] mb-4">
            Optimize & Scale Patient Acquisition
          </h3>
          <p className="text-gray-700 leading-relaxed">
            We continuously monitor your healthcare campaigns and provide daily performance reports. Our team identifies optimization opportunities, A/B tests medical creatives, and scales winning ads to maximize your return on ad spend and drive sustainable patient growth for your practice.
          </p>
        </div>
      </motion.div>
    </div>

  </div>
</section>


{/* FAQ SECTION */}
<section className="py-16 bg-white">
  <div className="max-w-7xl mx-auto px-6">
    
    {/* Section Header */}
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
    >
      <h2 className="text-3xl lg:text-4xl font-bold text-[#19354a]">
        Frequently Asked Questions about
      </h2>
      <h3 className="text-3xl lg:text-4xl font-bold text-[#19354a]">
        Healthcare Meta VA Services.
      </h3>
    </motion.div>

    {/* FAQ Content - Image Left, Questions Right */}
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      
      {/* Left - Image */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="flex justify-center lg:sticky lg:top-24"
      >
        <img 
          src="/images/faq-img.png" 
          alt="FAQ Healthcare Meta VA"
          className="w-full max-w-[220px] h-auto"
        />
      </motion.div>

      {/* Right - FAQ Items */}
      <div className="space-y-4">
        {[
          {
            question: "What is a Meta Virtual Assistant for Healthcare?",
            answer: "A Healthcare Meta Virtual Assistant is a dedicated professional who manages your medical practice's Facebook and Instagram advertising campaigns, handles patient inquiries, manages comments and DMs with HIPAA compliance, and ensures your healthcare social media presence runs smoothly 24/7."
          },
          {
            question: "How quickly will patient inquiries receive responses?",
            answer: "Our virtual assistants provide real-time response handling for healthcare practices. Most patient inquiries receive a response within minutes during business hours, ensuring no potential patient is left waiting and maximizing your appointment booking opportunities."
          },
          {
            question: "Is patient data handling HIPAA-compliant?",
            answer: "Absolutely. Our Meta Virtual Assistants are trained in HIPAA compliance protocols. We ensure all patient communications, data handling, and CRM updates follow strict healthcare privacy regulations to protect your practice and patients."
          },
          {
            question: "What reports will I receive about patient acquisition?",
            answer: "You'll receive daily performance reports covering ad metrics, patient inquiry counts, response times, appointment bookings, engagement rates, and cost per patient acquisition. Weekly and monthly summary reports with optimization recommendations are also provided."
          },
          {
            question: "How do I get started with your Healthcare Meta VA services?",
            answer: "Simply contact us for a free consultation. We'll assess your practice needs, understand your patient acquisition goals, and create a customized plan. Onboarding typically takes 2-3 days, and you'll have a dedicated healthcare-trained VA ready to manage your campaigns."
          }
        ].map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <details className="group">
              <summary className="flex items-center justify-between p-5 bg-[#19354a] rounded-lg cursor-pointer list-none">
                <span className="font-semibold text-white">{faq.question}</span>
                <span className="text-white text-2xl font-light group-open:hidden">+</span>
                <span className="text-white text-2xl font-light hidden group-open:block">−</span>
              </summary>
              <div className="px-5 py-4 bg-gray-50 rounded-b-lg text-gray-700 leading-relaxed -mt-2">
                {faq.answer}
              </div>
            </details>
          </motion.div>
        ))}
      </div>

    </div>
  </div>
</section>

      {/* CTA SECTION */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-4xl font-bold text-[#19354a] mb-6"
          >
            Ready to Grow Your Patient Base?
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 text-lg mb-8"
          >
            Let our healthcare Meta Virtual Assistant experts help you achieve sustainable practice growth and maximum patient acquisition ROI.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-[#19354a] text-white rounded-lg font-semibold hover:bg-[#0f2133] transition-all"
            >
              Get a Free Consultation
            </Link>
            <a 
              href="tel:+918920845297" 
              className="px-8 py-4 border-2 border-[#19354a] text-[#19354a] rounded-lg font-semibold hover:bg-[#19354a] hover:text-white transition-all"
            >
              Call Us Now
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MetaVirtualAssistant;